import parseToken from "./token";

export async function getWorkspaceId() {
  const { data } = await parseToken(getToken());
  if (!data) return null;
  return data["workspace_id"];
}

export async function getAccountFromToken() {
  const { data } = await parseToken(getToken());
  if (!data) return null;
  return data["account_id"];
}
const apiUrlBase = import.meta.env.VITE_WHITELABEL_API_URL_BASE;
const apiUrl = `${apiUrlBase}/api/workspaces/${await getWorkspaceId()}/`;

export class HTTPError extends Error {
  code: number;

  type: string | null;

  serverMessage: string | null;

  constructor(message: string, code: number, cause?: unknown) {
    super(message, { cause });
    this.name = "HTTPError";
    this.code = code;
    this.type = null;
    this.serverMessage = null;
  }
}

function headers(token: string, jsonContent = false) {
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(jsonContent && { "Content-Type": "application/json" }),
  };
}

function isJsonResponse(response: Response) {
  const contentType = response.headers.get("content-type");
  return contentType && contentType.indexOf("application/json") !== -1;
}

async function validateStatusCode(response: Response) {
  if (!response.ok) {
    // IF this message is ever changed, make sure to update sentry configuration, since it depends on the message
    // to ignore these errors
    const errorMessage = `Fetch error: Request to ${response.url} failed with status code: ${response.status}`;
    const error = new HTTPError(errorMessage, response.status, response.body);

    if (isJsonResponse(response)) {
      const jsonResponse = await response.json();
      const { error: serverErrorObject } = jsonResponse;

      error.serverMessage = serverErrorObject.message;
      error.type = serverErrorObject.type;
      throw error;
    } else {
      throw error;
    }
  }
  return response;
}

async function validateContentType(response: Response) {
  if (!isJsonResponse(response)) {
    throw new Error(`Invalid Content Type: ${response.status}`);
  }
  return response;
}

async function parseResponse(response: Response) {
  if (isJsonResponse(response)) {
    return response.json();
  }
  return { ...response };
}

async function parseError(response: Response) {
  return { ...response };
}

function getToken() {
  return location.hash.substring(1);
}

/**
 * Perform a GET HTTP request.
 * @public
 * @param {String} path The request path.
 * @returns {Promise<Any>} The request response.
 */

export function get(path: string) {
  //Change this if we get the JWT differently
  const token = getToken();

  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "GET",
    headers: headers(token),
  })
    .then(validateStatusCode)
    .then(validateContentType)
    .then(parseResponse)
    .catch(parseError);
}

export function post(path: string, data?: object) {
  const token = getToken();
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "POST",
    body: data ? JSON.stringify(data) : null,
    headers: headers(token, true),
  })
    .then(validateStatusCode)
    .then(parseResponse);
}

export function put(path: string, data?: object) {
  const token = getToken();
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "PUT",
    body: data ? JSON.stringify(data) : null,
    headers: headers(token, true),
  })
    .then(validateStatusCode)
    .then(parseResponse)
    .catch(parseError);
}

export function patch(path: string, data: object) {
  const token = getToken();
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "PATCH",
    body: data ? JSON.stringify(data) : null,
    headers: headers(token, true),
  })
    .then(validateStatusCode)
    .then(parseResponse);
}

export function del(path: string) {
  const token = getToken();
  return fetch(apiUrl ? `${apiUrl}${path}` : path, {
    method: "DELETE",
    headers: headers(token),
  })
    .then(validateStatusCode)
    .then(parseResponse)
    .catch(parseError);
}
